import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  FormHelperText,
  Paper,
} from '@mui/material';
import { ListingForm as ListingFormType } from '@/types';

interface PricingTabProps {
  formData: ListingFormType;
  errors: Record<string, string>;
  onFormDataChange: (updates: Partial<ListingFormType>) => void;
  onErrorsChange: (errors: Record<string, string>) => void;
  clearErrors: (fields: string[]) => void;
}

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
];

export const PricingTab: React.FC<PricingTabProps> = ({
  formData,
  errors,
  onFormDataChange,
  clearErrors,
}) => {
  const handlePricingChange = (field: string, value: any) => {
    onFormDataChange({
      pricing: {
        ...formData.pricing,
        [field]: value,
      },
    });
    clearErrors([`pricing.${field}`]);
  };

  const selectedCurrency = CURRENCIES.find(c => c.code === formData.pricing.currency);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Pricing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Set your nightly rate and additional fees to maximize your earnings while staying competitive.
      </Typography>

      <Grid container spacing={3}>
        {/* Base Pricing */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Base Pricing
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Currency</InputLabel>
            <Select
              value={formData.pricing.currency}
              onChange={(e) => handlePricingChange('currency', e.target.value)}
              label="Currency"
            >
              {CURRENCIES.map((currency) => (
                <MenuItem key={currency.code} value={currency.code}>
                  {currency.symbol} {currency.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label="Base Price per Night"
            value={formData.pricing.basePrice}
            onChange={(e) => handlePricingChange('basePrice', parseFloat(e.target.value) || 0)}
            error={!!errors['pricing.basePrice']}
            helperText={errors['pricing.basePrice'] || 'Your nightly rate before fees'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {selectedCurrency?.symbol}
                </InputAdornment>
              ),
            }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Additional Fees
          </Typography>
          <FormHelperText sx={{ mb: 2 }}>
            Optional fees that will be added to the total booking cost.
          </FormHelperText>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label="Cleaning Fee (Optional)"
            value={formData.pricing.cleaningFee || ''}
            onChange={(e) => handlePricingChange('cleaningFee', parseFloat(e.target.value) || 0)}
            helperText="One-time cleaning fee per stay"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {selectedCurrency?.symbol}
                </InputAdornment>
              ),
            }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label="Service Fee (Optional)"
            value={formData.pricing.serviceFee || ''}
            onChange={(e) => handlePricingChange('serviceFee', parseFloat(e.target.value) || 0)}
            helperText="Additional service fee per stay"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {selectedCurrency?.symbol}
                </InputAdornment>
              ),
            }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label="Tax Rate % (Optional)"
            value={formData.pricing.taxRate || ''}
            onChange={(e) => handlePricingChange('taxRate', parseFloat(e.target.value) || 0)}
            helperText="Local tax percentage"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">%</InputAdornment>
              ),
            }}
            inputProps={{ min: 0, max: 100, step: 0.01 }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Discounts
          </Typography>
          <FormHelperText sx={{ mb: 2 }}>
            Offer discounts for longer stays to attract more bookings.
          </FormHelperText>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Weekly Discount % (Optional)"
            value={formData.pricing.weeklyDiscount || ''}
            onChange={(e) => handlePricingChange('weeklyDiscount', parseFloat(e.target.value) || 0)}
            helperText="Discount for stays of 7+ nights"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">%</InputAdornment>
              ),
            }}
            inputProps={{ min: 0, max: 50, step: 1 }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Monthly Discount % (Optional)"
            value={formData.pricing.monthlyDiscount || ''}
            onChange={(e) => handlePricingChange('monthlyDiscount', parseFloat(e.target.value) || 0)}
            helperText="Discount for stays of 28+ nights"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">%</InputAdornment>
              ),
            }}
            inputProps={{ min: 0, max: 50, step: 1 }}
          />
        </Grid>

        {/* Pricing Summary */}
        {formData.pricing.basePrice > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Pricing Summary
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Base price per night:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedCurrency?.symbol}{formData.pricing.basePrice.toFixed(2)}
                  </Typography>
                </Box>
                {formData.pricing.cleaningFee && formData.pricing.cleaningFee > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Cleaning fee:</Typography>
                    <Typography variant="body2">
                      {selectedCurrency?.symbol}{formData.pricing.cleaningFee.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {formData.pricing.serviceFee && formData.pricing.serviceFee > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Service fee:</Typography>
                    <Typography variant="body2">
                      {selectedCurrency?.symbol}{formData.pricing.serviceFee.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {formData.pricing.weeklyDiscount && formData.pricing.weeklyDiscount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="success.main">Weekly discount:</Typography>
                    <Typography variant="body2" color="success.main">
                      -{formData.pricing.weeklyDiscount}%
                    </Typography>
                  </Box>
                )}
                {formData.pricing.monthlyDiscount && formData.pricing.monthlyDiscount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="success.main">Monthly discount:</Typography>
                    <Typography variant="body2" color="success.main">
                      -{formData.pricing.monthlyDiscount}%
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.contrastText">
              <strong>Pricing Tips:</strong> Research similar properties in your area to set competitive rates. Consider seasonal adjustments and local events that might affect demand. Offering discounts for longer stays can increase your booking rate and reduce turnover costs.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PricingTab;